import type {
  AppFeatureDefinition,
  FeatureRegistry,
  FeatureRegistryListener,
} from "../contracts";
import type { PermissionCatalogNode } from "../../permissions";
import { mergePermissionCatalogs } from "./mergePermissionCatalogs";

export function createFeatureRegistry(
  initial: readonly AppFeatureDefinition[] = [],
): FeatureRegistry {
  const modules = new Map<string, AppFeatureDefinition>();
  const listeners = new Set<FeatureRegistryListener>();

  // useSyncExternalStore compares snapshots by reference, so the array is
  // rebuilt only when the feature set actually changes.
  let snapshot: readonly AppFeatureDefinition[] = [];
  let permissionCatalog: readonly PermissionCatalogNode[] = [];

  function rebuildSnapshot(): void {
    snapshot = [...modules.values()].sort((a, b) => a.id.localeCompare(b.id));
    // Features root their catalog at the business-feature namespace, so several
    // contribute a `core` tree. Merge by segment rather than concatenating.
    permissionCatalog = mergePermissionCatalogs(
      snapshot.map((module) => module.permissionCatalog ?? []),
    );
  }

  function notify(): void {
    rebuildSnapshot();

    for (const listener of listeners) {
      listener();
    }
  }

  function register(definition: AppFeatureDefinition): void {
    const existing = modules.get(definition.id);

    if (existing !== undefined && existing.version !== definition.version) {
      // Re-registration is how a tenant feature gets hot-swapped; only warn when
      // a genuinely different build replaces one already in use.
      console.warn(
        `[registry] Module "${definition.id}" replaced: ${existing.version} -> ${definition.version}`,
      );
    }

    modules.set(definition.id, definition);
    notify();
  }

  for (const definition of initial) {
    modules.set(definition.id, definition);
  }

  rebuildSnapshot();

  return {
    register,

    registerAll(definitions) {
      for (const definition of definitions) {
        modules.set(definition.id, definition);
      }

      notify();
    },

    unregister(featureId) {
      const removed = modules.delete(featureId);

      if (removed) {
        notify();
      }

      return removed;
    },

    get: (featureId) => modules.get(featureId),
    list: () => snapshot,
    getSnapshot: () => snapshot,
    getPermissionCatalog: () => permissionCatalog,

    subscribe(listener) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
}
